import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { sensors } from '../services/api';
import './SensorDetail.css';

function SensorDetail() {
  const { id } = useParams();
  const [sensorData, setSensorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');
  const [chartType, setChartType] = useState('line');

  useEffect(() => {
    loadSensorData();
  }, [id, timeRange]);

  const loadSensorData = async () => {
    try {
      const params = {};
      if (timeRange === '24h') {
        params.startDate = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      } else if (timeRange === '7d') {
        params.startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      } else if (timeRange === '30d') {
        params.startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      }

      const response = await sensors.getData(id, params);
      setSensorData(response.data.map(item => ({
        ...item,
        formattedTime: format(new Date(item.timestamp), 'dd/MM HH:mm', { locale: ptBR })
      })));
    } catch (error) {
      toast.error('Erro ao carregar dados do sensor');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Carregando dados...</div>;

  return (
    <div className="sensor-detail-container">
      <h1>Dados do Sensor</h1>

      <div className="controls">
        <div className="time-range">
          <button
            className={timeRange === '24h' ? 'active' : ''}
            onClick={() => setTimeRange('24h')}
          >
            24 Horas
          </button>
          <button
            className={timeRange === '7d' ? 'active' : ''}
            onClick={() => setTimeRange('7d')}
          >
            7 Dias
          </button>
          <button
            className={timeRange === '30d' ? 'active' : ''}
            onClick={() => setTimeRange('30d')}
          >
            30 Dias
          </button>
        </div>

        <div className="chart-type">
          <button
            className={chartType === 'line' ? 'active' : ''}
            onClick={() => setChartType('line')}
          >
            Linha
          </button>
          <button
            className={chartType === 'bar' ? 'active' : ''}
            onClick={() => setChartType('bar')}
          >
            Barra
          </button>
        </div>
      </div>

      {sensorData.length > 0 ? (
        <>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              {chartType === 'line' ? (
                <LineChart data={sensorData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="formattedTime" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#8884d8"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              ) : (
                <BarChart data={sensorData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="formattedTime" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>

          <div className="table-container">
            <h2>Dados Recentes</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Valor</th>
                  <th>Unidade</th>
                </tr>
              </thead>
              <tbody>
                {sensorData.slice(0, 10).map((data) => (
                  <tr key={data._id}>
                    <td>{format(new Date(data.timestamp), 'dd/MM/yyyy HH:mm:ss')}</td>
                    <td>{data.value}</td>
                    <td>{data.unit || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p className="no-data">Nenhum dado disponível para este período</p>
      )}
    </div>
  );
}

export default SensorDetail;
